/**
 * Turning one patient's identity history into what the screen shows (DECISIONS.md #45).
 *
 * Pure functions, separate from the component, for the reason `patientOverviewDisplay.ts` gives:
 * the decisions worth testing without a DOM are here — what a version's fields read as, which of
 * them this version changed, and what a retraction says before the values are read.
 *
 * **{@link patientVersionFields} compares two versions and nothing more.** It is a derived
 * presentation of what the record already holds — the same five columns `PatientCorrection` writes,
 * laid out with a marker on the ones that moved. It draws no clinical conclusion, which is the line
 * `AGENTS.md` §3 rule 5 and `DECISIONS.md` #9 draw: *this name changed* is a fact about two rows,
 * not advice about a patient.
 *
 * **Nothing here is record content reformatted.** A name is shown as it was documented and a birth
 * date goes through the shared date formatter (AGENTS.md §5, I18N.md §3–4). The words this module
 * supplies are the labels, the standing, and `Ändrad i den här versionen`.
 *
 * `t` is a parameter, not an import (I18N.md §1): a module-level `t()` freezes whichever language
 * was selected when the bundle was first evaluated.
 *
 * **Two sentences under `patient:history` are word-for-word copies of `notes:` ones** — *Ersatt av en
 * senare version* and *Markerad som felaktigt införd* — and that is on purpose, unlike the field
 * labels above. The refusal wording is shared because the *rule* is shared and the two must not
 * drift; these two coincide only because neither sentence names its subject. A patient version and a
 * note version are different things being described, either may be reworded without the other, and a
 * patient screen reaching into the `notes` namespace for a sentence about demographics would make one
 * of them govern the other. If a third versioned thing appears, that is the point to move the
 * vocabulary to `common:` rather than to add a fourth copy.
 */

import type { TFunction } from 'i18next'

import { formatClinicalDateOnly, formatClinicalDateTime } from '../format/clinicalDate'
import { guarded } from './patientDisplay'
import { patientSex, practitionerName } from './patientOverviewDisplay'
import type { PatientVersion } from './patientVersionsApi'

/**
 * The lookup this screen needs.
 *
 * `registration` is one of the four for the reason `correctionDisplay.ts` gives about its own: the
 * history names the same five fields the correction form does, and `Förnamn` has to be `Förnamn` in
 * both places. A second copy of those labels under `patient:history` would be two strings for one
 * word with nothing keeping them equal.
 */
export type PatientHistoryTranslate = TFunction<
  ['common', 'patient', 'registration', 'correction']
>

/**
 * The columns a version is compared on.
 *
 * The five a correction can move, in the order the form asks for them, then the two it inherits.
 * The inherited pair is here for the reason `PatientVersions.cs` gives about returning it at all: a
 * reader comparing versions must be able to see that the status and the sekretessmarkering stayed
 * where they were.
 */
export type PatientHistoryFieldKey =
  | 'givenName'
  | 'familyName'
  | 'birthDate'
  | 'sex'
  | 'identity'
  | 'status'
  | 'confidentiality'

/** One field of one version, with whether this version is where it changed. */
export interface PatientHistoryField {
  /** Stable key for React and for the tests. */
  readonly key: PatientHistoryFieldKey

  readonly label: string

  /** The value as this version recorded it, in words. Record content is never reformatted. */
  readonly value: string

  /**
   * Whether this version is where the value changed.
   *
   * False on version 1 for every field — there is nothing behind it to have changed from, and a
   * first version marked as changed throughout would teach a reader to ignore the marker.
   */
  readonly changed: boolean
}

/**
 * How this version's status reads, before its values are read.
 *
 * `active` and `inactive` are not reported here: {@link patientVersionFields} carries the status as
 * a field, where a reader comparing versions can see it. What this reports is the one status that
 * changes how the version above must be read — a retraction — said before the name rather than
 * after it, matching `noteVersionException`. A reader who has already read a retracted name cannot
 * unread it.
 */
export function patientVersionException(
  version: PatientVersion,
  t: PatientHistoryTranslate,
): string | null {
  return version.status === 'entered-in-error' ? t('patient:history.retracted') : null
}

/** Whether this is the version in force, given the newest version number the patient has. */
export function patientVersionStanding(
  version: PatientVersion,
  newest: number,
  t: PatientHistoryTranslate,
): string {
  return version.version === newest
    ? t('patient:history.current')
    : t('patient:history.superseded')
}

/**
 * Who documented this version — the name, with the documented title before it when there is one.
 *
 * **No time on this line**, and that is the point of it. It used to read *läkare Demo Användare,
 * 2026-10-07 19:52*, where the time was `effectiveAt`: a correction inherits version 1's, so on
 * version 2 the name sat beside a time at which that author had written nothing. The browser pass
 * found it on 2026-10-07, beside a correctly labelled *Skriven i journalen 20:47* — two time axes
 * on one line with only one of them named, which is the reading AGENTS.md §4 says they must never
 * invite. Each time now has its own line and its own label: {@link patientVersionEffective} and
 * {@link patientVersionWritten}.
 *
 * The note history keeps *name, time*: a note is dated by the clinical moment it describes, which
 * is the convention a reader brings to a journal entry, and each of its versions has its own.
 *
 * The author is resolved to their **current** name, which `PatientVersionsQuery` documents as a
 * deliberate simplification: a practitioner who has since been renamed is named here as they are
 * now, not as they were when they wrote.
 */
export function patientVersionAuthor(
  version: PatientVersion,
  t: PatientHistoryTranslate,
): string {
  const name = practitionerName(version.authorGivenName, version.authorFamilyName, t)

  return version.authorRoleTitle === null
    ? t('patient:history.author', { name })
    : t('patient:history.authorWithTitle', { title: version.authorRoleTitle, name })
}

/**
 * When the identity this version records is clinically true — `effectiveAt`, said as such.
 *
 * The same on every version of one patient, because a correction inherits version 1's
 * (AGENTS.md §4). Shown anyway, and labelled, so that the time which does *not* move is never
 * mistaken for the one that does.
 */
export function patientVersionEffective(
  version: PatientVersion,
  t: PatientHistoryTranslate,
): string {
  return t('patient:history.effective', {
    time: guarded(() => formatClinicalDateTime(version.effectiveAt), t),
  })
}

/** When this version was written into the record. The value that differs between versions. */
export function patientVersionWritten(
  version: PatientVersion,
  t: PatientHistoryTranslate,
): string {
  return t('patient:history.written', {
    time: guarded(() => formatClinicalDateTime(version.recordedAt), t),
  })
}

/**
 * What this version recorded, field by field, each marked if this is where it changed.
 *
 * @param version the version being rendered
 * @param previous the version it supersedes, resolved by `versionId` rather than by subtracting
 *   one from the version number — `supersedes` is what the write actually recorded, and a history
 *   that inferred the link would be guessing at the one thing the column exists to state. Null for
 *   version 1, and for a version whose predecessor is somehow not in the list, which reads as
 *   nothing having changed rather than as everything having.
 */
export function patientVersionFields(
  version: PatientVersion,
  previous: PatientVersion | null,
  t: PatientHistoryTranslate,
): readonly PatientHistoryField[] {
  const changed = (pick: (from: PatientVersion) => unknown): boolean =>
    previous !== null && pick(previous) !== pick(version)

  // Narrowed into a local rather than asserted inside the literal below, so the formatter is only
  // reached with a string and the `null` branch is the catalogue's.
  const birthDate = version.birthDate

  return [
    {
      key: 'givenName',
      label: t('registration:field.givenName'),
      // Shown as documented, and `Ej angivet` where the identity was not established — an empty
      // cell in a name column reads as a rendering fault, which `patientName` says at length.
      value: version.givenName ?? t('common:value.notRecorded'),
      changed: changed((from) => from.givenName),
    },
    {
      key: 'familyName',
      label: t('registration:field.familyName'),
      value: version.familyName ?? t('common:value.notRecorded'),
      changed: changed((from) => from.familyName),
    },
    {
      key: 'birthDate',
      label: t('patient:overview.birthDate'),
      value:
        birthDate === null
          ? t('common:value.notRecorded')
          : guarded(() => formatClinicalDateOnly(birthDate), t),
      changed: changed((from) => from.birthDate),
    },
    {
      key: 'sex',
      label: t('patient:overview.sex'),
      value: patientSex(version.sex, t),
      changed: changed((from) => from.sex),
    },
    {
      // The field a history is usually opened for (`PatientVersion.isEstimatedIdentity`): the
      // version where this went false is the moment the record stopped hedging about who the
      // patient is.
      key: 'identity',
      label: t('patient:history.fieldIdentity'),
      value: version.isEstimatedIdentity
        ? t('patient:history.identityAssumed')
        : t('patient:history.identityEstablished'),
      changed: changed((from) => from.isEstimatedIdentity),
    },
    {
      key: 'status',
      label: t('patient:history.fieldStatus'),
      value: patientVersionStatus(version.status, t),
      changed: changed((from) => from.status),
    },
    {
      // Carried precisely because a correction inherits it rather than setting it. A reader
      // comparing two versions has to be able to see that a sekretessmarkering did **not** move,
      // and a field that is absent when it is unchanged cannot say that.
      key: 'confidentiality',
      label: t('common:safety.confidentialityMarked'),
      value: version.confidentialityMarked
        ? t('patient:history.yes')
        : t('patient:history.no'),
      changed: changed((from) => from.confidentialityMarked),
    },
  ]
}

/**
 * A patient status in words.
 *
 * The three values migration 0003's CHECK allows. The fallback shows the code, the same choice
 * `flagLabel` makes: a status a later migration permits must not render as a blank field on the one
 * screen whose job is to show what the record contains.
 */
function patientVersionStatus(status: string, t: PatientHistoryTranslate): string {
  switch (status) {
    case 'active':
      return t('patient:history.statusActive')
    case 'inactive':
      return t('patient:history.statusInactive')
    case 'entered-in-error':
      return t('patient:history.statusRetracted')
    default:
      return status
  }
}
